import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  USER_ACTIVITY_LOG_PAGESIZE_OPTIONS,
  USER_ACTIVITY_LOGS_TABLE_COLUMNS,
} from '@app-user-management/constants/user-activity-log.constants';
import { Subject } from 'rxjs';
import { UserActivityLogService } from '@app-user-management/services/user-activity-log/user-activity-log.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { DataService } from '@app-core/services/data/data.service';
import { DurationRange } from '@app-core/models/core.model';
import mixpanel from 'mixpanel-browser';
import { UserLog, UserLogListResponse, UsersLogListParams } from '@app-user-management/models/users.model';
import { getDurationFilterState, State } from 'src/app/home/reducers';
import { Store } from '@ngrx/store';
import { UpdateDurationFilter } from 'src/app/home/actions/duration-filter.actions';
import { DateService } from '@app-core/services/date/date.service';

@Component({
  selector: 'app-activity-log-list',
  templateUrl: './activity-log-list.component.html',
  styleUrls: ['./activity-log-list.component.scss'],
})
export class ActivityLogListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('paginator', { static: false })
  public paginator: MatPaginator;

  public loader = false;
  public tableColumns = USER_ACTIVITY_LOGS_TABLE_COLUMNS;
  public dataSource = new MatTableDataSource<UserLog>([]);
  public totalRows = 0;
  public pageSize = 10;
  public durationRange: DurationRange;
  public pageSizeOptions = USER_ACTIVITY_LOG_PAGESIZE_OPTIONS;

  private userId = '';
  private skip = 0;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    public activityLogService: UserActivityLogService,
    public dataService: DataService,
    private store: Store<State>,
    private dateService: DateService
  ) {}

  ngOnInit(): void {
    this.dataService._currentTablePageSize.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: number) => {
      if (value) {
        this.pageSize = value;
      }
    });

    this.store
      .select(getDurationFilterState)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((durationFilterState) => {
        this.durationRange = durationFilterState;
        this.onSearch();
      });

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.onUserCleared();
    });
  }

  ngAfterViewInit(): void {
    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.pageSize = event.pageSize;
      this.dataService._currentTablePageSize.next(event.pageSize);

      this.skip = event.pageIndex * event.pageSize;
      this.getData();
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getData() {
    this.loader = true;
    this.dataSource.data = new Array(this.pageSize).fill({});
    const params = new UsersLogListParams({
      limit: this.pageSize,
      offset: this.skip,
      userId: this.userId,
      startDate: this.dateService.toDaysStartISO(this.durationRange.startDate),
      endDate: this.dateService.toDaysEndISOPlusOne(this.durationRange.endDate),
    });

    this.activityLogService
      .getUserLogList(params)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: UserLogListResponse) => {
          this.dataSource.data = (res?.rows || []).map((row) => ({
            ...row,
            activityType: this.activityLogService.getActivityLabelByType(row.activityType),
          }));
          this.totalRows = res.totalCount;
        },
        () => {
          this.dataSource.data = [];
          this.totalRows = 0;
        }
      );
  }

  public navigate(log: UserLog) {
    this.track(`View Details Clicked - Activity log Tab / Users Page`, {
      'Log Id': log.logId,
    });

    this.router.navigate(['/user-management/activity-log'], {
      queryParams: {
        logId: log.logId,
      },
    });
  }

  public onSearch() {
    this.skip = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.getData();
  }

  public doRefresh() {
    this.skip = 0;
    this.paginator.pageIndex = 0;
    this.getData();
  }

  public durationChange(durationRange: DurationRange) {
    this.store.dispatch(new UpdateDurationFilter(durationRange));
    this.track(`Select date range - Activity Log Tab / Users Page`, {
      'Start Date': durationRange.startDate,
      'End Date': durationRange.endDate,
    });
  }

  onUserSelected(userId: string): void {
    this.userId = userId;
    this.onSearch();

    this.track(`Search User - Activity Log Tab / Users Page`, {
      'Search Value': this.userId,
    });
  }

  onUserCleared(): void {
    this.userId = '';
    this.onSearch();

    this.track(`Search User - Activity Log Tab / Users Page`, {
      'Search Value': '',
    });
  }

  track(...args: any[]) {
    try {
      mixpanel.track(...args);
    } catch (e) {
      // sometimes mixpanel throws error
      // just to ignore errors
    }
  }
}
